import React, { createContext, useContext, useMemo } from 'react';
import { collectionSources, CollectionSourceId } from '../config/collectionSources';
import * as data from '../services/fitbitCollectionDataService';
import * as exportApi from '../services/fitbitExtractionService';
import * as daily from '../services/fitbitDailyExtractionService';
import * as batch from '../services/fitbitExtractionBatch';
import { DeviceMetricProvider } from '../components/researcher/collection/metrics/DeviceMetricProvider';

type Services = ReturnType<typeof data.createCollectionDataService> &
  ReturnType<typeof exportApi.createCollectionExportService> & ReturnType<typeof daily.createCollectionDailyService> & {
    source: (typeof collectionSources)[CollectionSourceId];
    submitFitbitExtractionBatch: typeof batch.submitFitbitExtractionBatch;
  };
const Context = createContext<Services | null>(null);
const fitbit = (): Services => ({ ...data, ...exportApi, ...daily,
  source: collectionSources.fitbit, submitFitbitExtractionBatch: batch.submitFitbitExtractionBatch });

export function CollectionSourceProvider({ source, children }: { source: CollectionSourceId; children: React.ReactNode }) {
  const services = useMemo<Services>(() => {
    if (source !== 'ultrahuman') return { ...fitbit(), source: collectionSources[source] };
    const exportService = exportApi.createCollectionExportService(source);
    return { ...data.createCollectionDataService(source), ...exportService, ...daily.createCollectionDailyService(source),
      source: collectionSources[source],
      submitFitbitExtractionBatch: (studyId, items, active, progress) =>
        batch.submitFitbitExtractionBatch(studyId, items, active, progress, exportService.createFitbitExtraction) };
  }, [source]);
  return <Context.Provider value={services}><DeviceMetricProvider provider={source === 'healthkit' ? null : source}>
    {children}
  </DeviceMetricProvider></Context.Provider>;
}

export function useCollectionServices(): Services { return useContext(Context) || fitbit(); }
